import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseArrayParam } from '../utils/filterUtils';

// Create context
const FilterContext = createContext();

// Initial filter state
const initialFilterState = {
  search: '',
  isActive: null,
  roles: [],
  providers: [],
  dateRange: {
    start: null,
    end: null
  },
  page: 1,
  limit: 10
};

/**
 * Provider component for filter state management
 */
export const FilterProvider = ({ children }) => {
  // State for filters
  const [filters, setFilters] = useState(initialFilterState);
  // Get/set URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  // Track active filter count
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Initialize filters from URL on mount
  useEffect(() => {
    // Convert URL params to filter state
    const urlIsActive = searchParams.get('isActive');
    let isActive = null;
    if (urlIsActive === 'true') isActive = true;
    else if (urlIsActive === 'false') isActive = false;
    
    const urlFilters = {
      search: searchParams.get('search') || '',
      isActive,
      roles: parseArrayParam(searchParams.getAll('roles')),
      providers: parseArrayParam(searchParams.getAll('providers')),
      dateRange: {
        start: searchParams.get('createdDateStart') || null,
        end: searchParams.get('createdDateEnd') || null
      },
      page: parseInt(searchParams.get('page'), 10) || 1,
      limit: parseInt(searchParams.get('limit'), 10) || 10
    };
    
    setFilters(urlFilters);
    updateActiveFilterCount(urlFilters);
    // Only run this effect on mount
  }, []);

  // Update URL when filters change
  useEffect(() => {
    // Get current params (including sorting params)
    const params = {};
    searchParams.forEach((value, key) => {
      // Keep sorting params and other params not managed by FilterContext
      if (!['search', 'isActive', 'roles', 'providers', 'createdDateStart', 
            'createdDateEnd', 'page', 'limit'].includes(key)) {
        params[key] = value;
      }
    });
    
    // Add filter params
    if (filters.search) params.search = filters.search;
    
    if (filters.isActive !== null && filters.isActive !== undefined) {
      params.isActive = filters.isActive != null ? filters.isActive.toString() : '';
    }
    
    // Handle array parameters
    if (filters.roles && filters.roles.length > 0) {
      params.roles = filters.roles;
    }
    
    if (filters.providers && filters.providers.length > 0) {
      params.providers = filters.providers;
    }
    
    // Add date range params
    if (filters.dateRange?.start) {
      params.createdDateStart = filters.dateRange.start != null ? filters.dateRange.start.toString() : '';
    }
    
    if (filters.dateRange?.end) {
      params.createdDateEnd = filters.dateRange.end != null ? filters.dateRange.end.toString() : '';
    }
    
    // Add pagination params
    if (filters.page !== 1 && filters.page != null) {
      params.page = filters.page != null ? filters.page.toString() : '1';
    }
    
    if (filters.limit !== 10 && filters.limit != null) {
      params.limit = filters.limit != null ? filters.limit.toString() : '10';
    }
    
    // Update URL without triggering navigation
    setSearchParams(params, { replace: true });
    
    // Update active filter count
    updateActiveFilterCount(filters);
  }, [filters, setSearchParams, searchParams]);

  // Calculate number of active filters
  const updateActiveFilterCount = (filterState) => {
    let count = 0;
    // Only count filters that are truly user-applied (not default values)
    if (filterState.search && filterState.search.trim() !== '') count++;
    if (filterState.isActive === true || filterState.isActive === false) count++;
    if (Array.isArray(filterState.roles) && filterState.roles.length > 0) count++;
    if (Array.isArray(filterState.providers) && filterState.providers.length > 0) count++;
    if ((filterState.dateRange && filterState.dateRange.start) || (filterState.dateRange && filterState.dateRange.end)) count++;
    setActiveFilterCount(count);
  };

  // Set a single filter value
  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when changing filters
      page: key === 'page' ? value : 1
    }));
  }, []);

  // Set date range filter
  const setDateRange = useCallback((start, end) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { start, end },
      page: 1
    }));
  }, []);

  // Toggle a value in array filter (roles, providers)
  const toggleArrayFilter = useCallback((key, value) => {
    setFilters(prev => {
      const currentArray = prev[key] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
        
      return {
        ...prev,
        [key]: newArray,
        page: 1
      };
    });
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  // Clear a specific filter
  const clearFilter = useCallback((key) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      switch (key) {
        case 'search':
          newFilters.search = '';
          break;
        case 'isActive':
          newFilters.isActive = null;
          break;
        case 'roles':
          newFilters.roles = [];
          break;
        case 'providers':
          newFilters.providers = [];
          break;
        case 'dateRange':
          newFilters.dateRange = { start: null, end: null };
          break;
        default:
          break;
      }
      
      return {
        ...newFilters,
        page: 1
      };
    });
  }, []);

  // Context value
  const value = {
    filters,
    setFilter,
    setDateRange,
    toggleArrayFilter,
    resetFilters,
    clearFilter,
    activeFilterCount
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

// Custom hook to use the filter context
export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

export default FilterContext; 